import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_sentryserver_caae03aa from 'nuxt_plugin_sentryserver_caae03aa' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_34ef8ca3 from 'nuxt_plugin_sentryclient_34ef8ca3' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_googletagmanager_2608876b from 'nuxt_plugin_googletagmanager_2608876b' // Source: ./google-tag-manager.js (mode: 'client')
import nuxt_plugin_workbox_0b57667a from 'nuxt_plugin_workbox_0b57667a' // Source: ./workbox.js (mode: 'client')
import nuxt_plugin_metaplugin_68c220d6 from 'nuxt_plugin_metaplugin_68c220d6' // Source: ./pwa/meta.plugin.js (mode: 'all')
import nuxt_plugin_axios_21d8d532 from 'nuxt_plugin_axios_21d8d532' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_ventratacontroller_5f660098 from 'nuxt_plugin_ventratacontroller_5f660098' // Source: ../plugins/ventrata.controller.js (mode: 'all')
import nuxt_plugin_veevalidate_6e5ad03a from 'nuxt_plugin_veevalidate_6e5ad03a' // Source: ../plugins/vee-validate (mode: 'all')
import nuxt_plugin_imglazyloader_669a3968 from 'nuxt_plugin_imglazyloader_669a3968' // Source: ../plugins/img-lazy-loader (mode: 'all')
import nuxt_plugin_localstorage_63be1e63 from 'nuxt_plugin_localstorage_63be1e63' // Source: ../plugins/local-storage (mode: 'all')
import nuxt_plugin_svgicon_79745ca7 from 'nuxt_plugin_svgicon_79745ca7' // Source: ../plugins/svgicon (mode: 'all')
import nuxt_plugin_ventrataclient_2ce1bcb6 from 'nuxt_plugin_ventrataclient_2ce1bcb6' // Source: ../plugins/ventrata.client.js (mode: 'client')
import nuxt_plugin_vwo_2fdbbf42 from 'nuxt_plugin_vwo_2fdbbf42' // Source: ../plugins/vwo.js (mode: 'client')
import nuxt_plugin_unveilhooks_ed8c69a2 from 'nuxt_plugin_unveilhooks_ed8c69a2' // Source: ../plugins/unveilhooks (mode: 'client')
import nuxt_plugin_vuexcache_01a9dd9d from 'nuxt_plugin_vuexcache_01a9dd9d' // Source: ../plugins/vuex-cache (mode: 'client')
import nuxt_plugin_persistedstate_56792181 from 'nuxt_plugin_persistedstate_56792181' // Source: ../plugins/persisted-state (mode: 'client')
import nuxt_plugin_jqueryplugin_9c4d94e8 from 'nuxt_plugin_jqueryplugin_9c4d94e8' // Source: ../plugins/jquery-plugin (mode: 'client')
import nuxt_plugin_vuexinitclient_b0c1599a from 'nuxt_plugin_vuexinitclient_b0c1599a' // Source: ../plugins/vuex-init.client.js (mode: 'client')
import nuxt_plugin_functionalcalendar_722b9c25 from 'nuxt_plugin_functionalcalendar_722b9c25' // Source: ../plugins/functional-calendar.js (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$nuxt', {
  get() {
    const globalNuxt = this.$root.$options.$nuxt
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$nuxt
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const router = await createRouter(ssrContext, config)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"}],"link":[{"rel":"icon","type":"image\u002Fx-icon","href":"\u002Ffavicon.ico"},{"rel":"icon","type":"image\u002Fpng","sizes":"16x16","href":"\u002Ffavicon-16x16.png"},{"rel":"icon","type":"image\u002Fpng","sizes":"32x32","href":"\u002Ffavicon-32x32.png"},{"rel":"dns-prefetch","href":"https:\u002F\u002Fgeoip-js.com"},{"rel":"preconnect","href":"https:\u002F\u002Fimages.ctfassets.net"},{"rel":"preconnect","href":"https:\u002F\u002Fdev.visualwebsiteoptimizer.com"},{"rel":"preload","href":"\u002Ffonts\u002FVollkorn-700-latin34.woff2","as":"font","type":"font\u002Fwoff2","crossorigin":true},{"rel":"preload","href":"\u002Ffonts\u002FVollkorn-400-latin22.woff2","as":"font","type":"font\u002Fwoff2","crossorigin":true},{"rel":"preload","href":"\u002Ffonts\u002FVollkorn-600-latin28.woff2","as":"font","type":"font\u002Fwoff2","crossorigin":true},{"rel":"preload","href":"\u002Ffonts\u002FOpen_Sans-400-latin8.woff2","as":"font","type":"font\u002Fwoff2","crossorigin":true},{"rel":"preload","href":"\u002Ffonts\u002FOpen_Sans-600-latin16.woff2","as":"font","type":"font\u002Fwoff2","crossorigin":true}],"script":[{"src":"\u002Fjs\u002FgeoIP.js","type":"text\u002Fjavascript","body":true},{"src":"\u002Fjs\u002FgtmLocation.js","type":"text\u002Fjavascript","body":true},{"src":"\u002F\u002Fwww.googletagmanager.com\u002Fgtm.js?id=GTM-NT7DS5N&l=dataLayer","async":true}],"style":[],"noscript":[{"hid":"gtm-noscript","innerHTML":"\u003Ciframe src=\"\u002F\u002Fwww.googletagmanager.com\u002Fns.html?id=GTM-NT7DS5N&l=dataLayer\" height=\"0\" width=\"0\" style=\"display:none;visibility:hidden\"\u003E\u003C\u002Fiframe\u003E","pbody":true}],"__dangerouslyDisableSanitizersByTagID":{"gtm-noscript":["innerHTML"]}},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (process.server && typeof nuxt_plugin_sentryserver_caae03aa === 'function') {
    await nuxt_plugin_sentryserver_caae03aa(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_34ef8ca3 === 'function') {
    await nuxt_plugin_sentryclient_34ef8ca3(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_googletagmanager_2608876b === 'function') {
    await nuxt_plugin_googletagmanager_2608876b(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_workbox_0b57667a === 'function') {
    await nuxt_plugin_workbox_0b57667a(app.context, inject)
  }

  if (typeof nuxt_plugin_metaplugin_68c220d6 === 'function') {
    await nuxt_plugin_metaplugin_68c220d6(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_21d8d532 === 'function') {
    await nuxt_plugin_axios_21d8d532(app.context, inject)
  }

  if (typeof nuxt_plugin_ventratacontroller_5f660098 === 'function') {
    await nuxt_plugin_ventratacontroller_5f660098(app.context, inject)
  }

  if (typeof nuxt_plugin_veevalidate_6e5ad03a === 'function') {
    await nuxt_plugin_veevalidate_6e5ad03a(app.context, inject)
  }

  if (typeof nuxt_plugin_imglazyloader_669a3968 === 'function') {
    await nuxt_plugin_imglazyloader_669a3968(app.context, inject)
  }

  if (typeof nuxt_plugin_localstorage_63be1e63 === 'function') {
    await nuxt_plugin_localstorage_63be1e63(app.context, inject)
  }

  if (typeof nuxt_plugin_svgicon_79745ca7 === 'function') {
    await nuxt_plugin_svgicon_79745ca7(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_ventrataclient_2ce1bcb6 === 'function') {
    await nuxt_plugin_ventrataclient_2ce1bcb6(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vwo_2fdbbf42 === 'function') {
    await nuxt_plugin_vwo_2fdbbf42(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_unveilhooks_ed8c69a2 === 'function') {
    await nuxt_plugin_unveilhooks_ed8c69a2(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuexcache_01a9dd9d === 'function') {
    await nuxt_plugin_vuexcache_01a9dd9d(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_persistedstate_56792181 === 'function') {
    await nuxt_plugin_persistedstate_56792181(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_jqueryplugin_9c4d94e8 === 'function') {
    await nuxt_plugin_jqueryplugin_9c4d94e8(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuexinitclient_b0c1599a === 'function') {
    await nuxt_plugin_vuexinitclient_b0c1599a(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_functionalcalendar_722b9c25 === 'function') {
    await nuxt_plugin_functionalcalendar_722b9c25(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    const { route } = router.resolve(app.context.route.fullPath)
    // Ignore 404s rather than blindly replacing URL
    if (!route.matched.length && process.client) {
      return resolve()
    }
    router.replace(route, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
